/* eslint-disable import/no-cycle */
import {computed, decorate, observable} from 'mobx';
import {joinFieldName} from '../../../../shared/form/formFields';
import i18n from '../../../../../i18n/i18n';
import WidgetSettingFormField from '../../../../shared/widgets/WidgetSettingFormField';
import {ValidationRules} from '../../../../../utils/validationRules';
import {CockpitWidgetConfig} from './cockpitWidgetConfig';
import {BaseModel} from '../../../../../models/base';

export class timeseriesCockpitWidgetConfig extends CockpitWidgetConfig {
  selectedHierarchyId;

  constructor({rootStore, widgetTypeIdentifier, widgetId}) {
    super({rootStore, widgetTypeIdentifier, widgetId});
  }

  static onLoadConfig = (rootStore) => {
    rootStore.sensorStore.loadAll();
  };

  static widgetType = {
    identifier: 'timeseries-chart',
    name: i18n.t('cockpitWidget.widgetTypeIdentifier.timeseries'),
  };

  get tabs() {
    return [
      this.generalSettingsTab,
      this.workplaceTab,
      {
        key: 'timeseries',
        label: i18n.t('widget.WidgetSettingsModal.timeseriesConfigTabTitle'),
      },
    ];
  }

  workplace = [
    {
      id: 'hierarchyId',
      label: i18n.t('cockpitWidget.settings.workplace.label'),
      helpText: i18n.t('cockpitWidget.settings.workplace.help'),
      type: WidgetSettingFormField.fieldTypes.HierarchyTreeWorkplace,
    },
  ];

  get timeseries() {
    return [
      {
        id: 'showLegend',
        label: i18n.t('timeseriesCockpitWidgetConfig.settings.showLegend.label'),
        type: WidgetSettingFormField.fieldTypes.Boolean,
        defaultValue: false,
      },
      {
        id: 'sensor',
        label: i18n.t('timeseriesCockpitWidgetConfig.settings.sensor.label'),
        type: WidgetSettingFormField.fieldTypes.Select,
        options: this.sensors,
        validations: [ValidationRules.required],
        onFormChange: (form, changedFields) => {
          const field = 'hierarchyId';
          if (changedFields.find((f) => field === joinFieldName(f.name))) {
            form.setFieldsValue({sensor: null});
          }
        },
      },
      {
        id: 'yAxisUnit',
        label: i18n.t('timeseriesCockpitWidgetConfig.settings.yAxisUnit.label'),
        type: WidgetSettingFormField.fieldTypes.String,
        validations: [ValidationRules.required],
      },
      {
        id: 'yAxisMin',
        label: i18n.t('timeseriesCockpitWidgetConfig.settings.yAxisMin.label'),
        type: WidgetSettingFormField.fieldTypes.Number,
      },
      {
        id: 'yAxisMax',
        label: i18n.t('timeseriesCockpitWidgetConfig.settings.yAxisMax.label'),
        type: WidgetSettingFormField.fieldTypes.Number,
      },
      {
        id: 'xAxisHours',
        label: i18n.t('timeseriesCockpitWidgetConfig.settings.xAxisHours.label'),
        type: WidgetSettingFormField.fieldTypes.Number,
        unit: i18n.t('timeseriesCockpitWidgetConfig.settings.xAxisHours.unit'),
        validations: [ValidationRules.required, ValidationRules.lessThanOrEqualTo(168)],
        defaultValue: 8,
      },
      {
        id: 'decimalPlaces',
        label: i18n.t('timeseriesCockpitWidgetConfig.settings.decimalPlaces.label'),
        type: WidgetSettingFormField.fieldTypes.Number,
        validations: [ValidationRules.greaterThanOrEqualTo(0), ValidationRules.lessThanOrEqualTo(100)],
        defaultValue: 3,
      },
      {
        id: 'resolution',
        label: i18n.t('timeseriesCockpitWidgetConfig.settings.resolution.label'),
        helpText: i18n.t('timeseriesCockpitWidgetConfig.settings.resolution.help'),
        unit: i18n.t('timeseriesCockpitWidgetConfig.settings.resolution.unit'),
        type: WidgetSettingFormField.fieldTypes.Number,
        validations: [ValidationRules.greaterThanOrEqualTo(5), ValidationRules.lessThanOrEqualTo(60)],
        defaultValue: '',
      },
    ];
  }

  get sensors() {
    const getDashboardHierarchy = () => (this.rootStore.workplaceStore.workplaces.find(
      (workplace) => workplace.id === this.getRelevantWorkplaceId()
    )?.hierarchyId);
    const getDefaultHierarchy = () => (this.rootStore.hierarchyStore.hierarchyRoot?.id);
    const hierarchyId = this.selectedHierarchyId || getDashboardHierarchy() || getDefaultHierarchy();

    if (hierarchyId) {
      const hierarchyChildren = BaseModel.findChildrenByParent(
        hierarchyId,
        this.rootStore.hierarchyStore.hierarchies,
        'parentId',
        null
      );
      const hierarchyChildrenIds = hierarchyChildren.length ? hierarchyChildren.map((h) => h.id) : [];
      const hierarchyIds = [hierarchyId, ...hierarchyChildrenIds];
      return this.rootStore.sensorStore.sensors.filter((sensor) => (
        sensor.hierarchyId === null || hierarchyIds.includes(sensor.hierarchyId)
      )).map((sensor) => ({
        value: sensor.name,
        label: sensor.label || sensor.name,
      }));
    }
    return [];
  }
}

decorate(timeseriesCockpitWidgetConfig, {
  selectedHierarchyId: observable,
  sensors: computed,
  timeseries: computed,
});
